import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";
import { MapContainer, TileLayer, Marker, Popup, Polygon, useMap, GeoJSON, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { 
  Map,
  Plus,
  ArrowLeft,
  Layers,
  MapPin,
  Square,
  Eye,
  EyeOff,
  Trash2,
  Edit,
  Save,
  RefreshCw,
  X,
  Check
} from "lucide-react";

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png"
});

// Horse Mountain Ranch center coordinates
const HORSE_MOUNTAIN_CENTER = [39.781180, -106.701006];
const DEFAULT_ZOOM = 16;

// Map controller component
const MapController = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || DEFAULT_ZOOM);
    }
  }, [center, zoom, map]);
  return null;
};

// Draggable marker component for creating/editing lots
const DraggableMarker = ({ position, onPositionChange, draggable = true }) => {
  const markerRef = useRef(null);
  
  const eventHandlers = {
    dragend() {
      const marker = markerRef.current;
      if (marker != null) {
        const latlng = marker.getLatLng();
        onPositionChange(latlng.lat, latlng.lng);
      }
    },
  };

  return (
    <Marker
      draggable={draggable}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
    >
      <Popup>
        <div className="text-center">
          <strong>Drag to position</strong><br/>
          <small>{position[0].toFixed(6)}, {position[1].toFixed(6)}</small>
        </div>
      </Popup>
    </Marker>
  );
};

// Map click handler for setting marker position
const MapClickHandler = ({ onClick }) => {
  useMapEvents({
    click: (e) => {
      onClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const GISPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const isBoard = user?.role === "admin" || user?.role === "board" || user?.role === "property_manager";
  
  // State
  const [lots, setLots] = useState([]);
  const [layers, setLayers] = useState([]);
  const [geojson, setGeojson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLot, setSelectedLot] = useState(null);
  const [mapCenter, setMapCenter] = useState(HORSE_MOUNTAIN_CENTER);
  
  // Layer visibility
  const [showLots, setShowLots] = useState(true);
  const [showEasements, setShowEasements] = useState(true);
  const [showEnvelopes, setShowEnvelopes] = useState(false);
  const [showMarkers, setShowMarkers] = useState(true);
  
  // Create lot dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [markerPosition, setMarkerPosition] = useState(HORSE_MOUNTAIN_CENTER);
  const [newLot, setNewLot] = useState({
    lot_number: "",
    latitude: HORSE_MOUNTAIN_CENTER[0].toString(),
    longitude: HORSE_MOUNTAIN_CENTER[1].toString(),
    acreage: "",
    notes: ""
  });

  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [editingLot, setEditingLot] = useState(null);
  const [editMarkerPosition, setEditMarkerPosition] = useState(null);

  const fetchLots = async () => {
    try {
      const response = await axios.get(`${API}/gis/lots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLots(response.data);
    } catch (error) {
      console.error("Failed to fetch lots:", error);
    }
  };

  const fetchLayers = async () => {
    try {
      const response = await axios.get(`${API}/gis/layers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLayers(response.data);
    } catch (error) {
      console.error("Failed to fetch layers:", error);
    }
  };

  const fetchGeojson = async () => {
    try {
      const response = await axios.get(`${API}/gis/properties-geojson`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeojson(response.data);
    } catch (error) {
      console.error("Failed to fetch GeoJSON:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLots(), fetchLayers(), fetchGeojson()]);
      setLoading(false);
    };
    loadData();
  }, [token]);

  // Update marker position when dialog opens
  useEffect(() => {
    if (createOpen) {
      setMarkerPosition(HORSE_MOUNTAIN_CENTER);
      setNewLot({
        lot_number: "",
        latitude: HORSE_MOUNTAIN_CENTER[0].toString(),
        longitude: HORSE_MOUNTAIN_CENTER[1].toString(),
        acreage: "",
        notes: ""
      });
    }
  }, [createOpen]);

  // Handle marker drag in create mode
  const handleCreateMarkerDrag = (lat, lng) => {
    setMarkerPosition([lat, lng]);
    setNewLot(prev => ({
      ...prev,
      latitude: lat.toFixed(6),
      longitude: lng.toFixed(6)
    }));
  };

  // Handle edit marker drag
  const handleEditMarkerDrag = (lat, lng) => {
    setEditMarkerPosition([lat, lng]);
  };

  const handleCreateLot = async () => {
    if (!newLot.lot_number) {
      toast.error("Lot number is required");
      return;
    }
    
    try {
      const payload = {
        lot_number: newLot.lot_number,
        acreage: newLot.acreage ? parseFloat(newLot.acreage) : null,
        notes: newLot.notes || null
      };
      
      // Use marker position for coordinates
      payload.center = {
        latitude: parseFloat(newLot.latitude) || markerPosition[0],
        longitude: parseFloat(newLot.longitude) || markerPosition[1]
      };
      
      await axios.post(`${API}/gis/lots`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Lot created successfully");
      setCreateOpen(false);
      setNewLot({ lot_number: "", latitude: "", longitude: "", acreage: "", notes: "" });
      fetchLots();
      fetchGeojson();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create lot");
    }
  };

  const handleDeleteLot = async (lotId) => {
    if (!confirm("Are you sure you want to delete this lot?")) return;
    
    try {
      await axios.delete(`${API}/gis/lots/${lotId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Lot deleted");
      fetchLots();
      fetchGeojson();
      setSelectedLot(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete lot");
    }
  };

  const handleLotClick = (lot) => {
    setSelectedLot(lot);
    if (lot.center) {
      setMapCenter([lot.center.latitude, lot.center.longitude]);
    }
  };

  // Enter edit mode for a lot
  const handleEditLot = (lot) => {
    setEditingLot(lot);
    setEditMode(true);
    if (lot.center) {
      setEditMarkerPosition([lot.center.latitude, lot.center.longitude]);
      setMapCenter([lot.center.latitude, lot.center.longitude]);
    } else {
      setEditMarkerPosition(HORSE_MOUNTAIN_CENTER);
    }
  };

  // Save edited lot
  const handleSaveEdit = async () => {
    if (!editingLot || !editMarkerPosition) return;
    
    try {
      await axios.put(`${API}/gis/lots/${editingLot.lot_id}`, {
        center: {
          latitude: editMarkerPosition[0],
          longitude: editMarkerPosition[1]
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Lot updated successfully");
      setEditMode(false);
      setEditingLot(null);
      setEditMarkerPosition(null);
      fetchLots();
      fetchGeojson();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update lot");
    }
  };

  // Cancel edit mode
  const handleCancelEdit = () => {
    setEditMode(false);
    setEditingLot(null);
    setEditMarkerPosition(null);
  };

  // Style function for GeoJSON features
  const getFeatureStyle = (feature) => {
    return {
      fillColor: "#2F4F4F",
      fillOpacity: 0.3,
      color: "#C49A6C",
      weight: 2
    };
  };

  // Popup for GeoJSON features
  const onEachFeature = (feature, layer) => {
    if (feature.properties) {
      const props = feature.properties;
      layer.bindPopup(`
        <div style="min-width: 150px;">
          <strong>Lot ${props.lot_number || 'N/A'}</strong><br/>
          ${props.address ? `<small>${props.address}</small><br/>` : ''}
          ${props.acreage ? `<small>${props.acreage} acres</small>` : ''}
        </div>
      `);
    }
  };

  const content = (
    <div className="min-h-screen bg-background">
      {/* Page Title for Board - shows with sidebar */}
      {isBoard && (
        <div className="p-6 lg:p-8 pb-0">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Map className="w-5 h-5 text-primary" />
            </div>
            <div>
              <span className="font-serif text-lg font-semibold block">GIS Property Map</span>
              <span className="text-xs text-muted-foreground">Horse Mountain Ranch - Interactive lot boundaries</span>
            </div>
          </div>
        </div>
      )}

      {/* Header for non-board users */}
      {!isBoard && (
        <header className="border-b bg-card/95 backdrop-blur-sm sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/owner")} className="text-muted-foreground hover:text-foreground transition-colors duration-200">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Map className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <span className="font-serif text-lg font-semibold block">GIS Property Map</span>
                  <span className="text-xs text-muted-foreground">Horse Mountain Ranch - Interactive lot boundaries</span>
                </div>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Action bar with controls */}
      <div className={isBoard ? "px-6 lg:px-8 pb-4" : "max-w-7xl mx-auto px-4 md:px-8 py-4"}>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {editMode && (
            <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-700">
              Edit Mode Active
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={() => { fetchLots(); fetchGeojson(); }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {isBoard && !editMode && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-lot-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Lot
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Add New Lot</DialogTitle>
                  <DialogDescription>
                    Drag the marker on the map to set the lot location, or enter coordinates manually
                  </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4 py-4">
                  {/* Mini map for marker placement */}
                  <div className="col-span-2 h-[250px] rounded-lg overflow-hidden border">
                    <MapContainer
                      center={HORSE_MOUNTAIN_CENTER}
                      zoom={DEFAULT_ZOOM}
                      style={{ height: "100%", width: "100%" }}
                    >
                        <TileLayer
                          attribution='&copy; OpenStreetMap'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <DraggableMarker 
                          position={markerPosition}
                          onPositionChange={handleCreateMarkerDrag}
                        />
                        <MapClickHandler onClick={handleCreateMarkerDrag} />
                      </MapContainer>
                    </div>
                    <p className="col-span-2 text-xs text-muted-foreground text-center">
                      Click on the map or drag the marker to set the lot centroid
                    </p>
                    
                    <div className="space-y-2">
                      <Label>Lot Number *</Label>
                      <Input
                        value={newLot.lot_number}
                        onChange={(e) => setNewLot({...newLot, lot_number: e.target.value})}
                        placeholder="e.g., 101"
                        data-testid="lot-number-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Acreage</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newLot.acreage}
                        onChange={(e) => setNewLot({...newLot, acreage: e.target.value})}
                        placeholder="2.5"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Latitude</Label>
                      <Input
                        type="number"
                        step="any"
                        value={newLot.latitude}
                        onChange={(e) => {
                          setNewLot({...newLot, latitude: e.target.value});
                          if (e.target.value && newLot.longitude) {
                            setMarkerPosition([parseFloat(e.target.value), parseFloat(newLot.longitude)]);
                          }
                        }}
                        placeholder="39.781180"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Longitude</Label>
                      <Input
                        type="number"
                        step="any"
                        value={newLot.longitude}
                        onChange={(e) => {
                          setNewLot({...newLot, longitude: e.target.value});
                          if (newLot.latitude && e.target.value) {
                            setMarkerPosition([parseFloat(newLot.latitude), parseFloat(e.target.value)]);
                          }
                        }}
                        placeholder="-106.701006"
                      />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Notes</Label>
                      <Textarea
                        value={newLot.notes}
                        onChange={(e) => setNewLot({...newLot, notes: e.target.value})}
                        placeholder="Internal notes about this lot"
                        rows={2}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreateLot} data-testid="save-lot-btn">
                      <Save className="w-4 h-4 mr-2" />
                      Create Lot
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

      <div className={isBoard ? "px-6 lg:px-8 py-6" : "max-w-7xl mx-auto px-4 md:px-8 py-6"}>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Layers & Lots */}
          <div className="lg:col-span-1 space-y-4">
            {/* Edit Mode Controls */}
            {editMode && editingLot && (
              <Card className="border-yellow-500/50 bg-yellow-500/5">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Edit className="w-4 h-4" />
                    Editing Lot {editingLot.lot_number}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-muted-foreground">
                    Drag the marker to reposition the lot centroid
                  </p>
                  {editMarkerPosition && (
                    <div className="text-xs">
                      <p>Lat: {editMarkerPosition[0].toFixed(6)}</p>
                      <p>Lng: {editMarkerPosition[1].toFixed(6)}</p>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleSaveEdit} className="flex-1">
                      <Check className="w-4 h-4 mr-1" />
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleCancelEdit} className="flex-1">
                      <X className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Layer Controls */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Layers className="w-4 h-4" />
                  Map Layers
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Lot Boundaries</Label>
                  <Switch checked={showLots} onCheckedChange={setShowLots} />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Lot Markers</Label>
                  <Switch checked={showMarkers} onCheckedChange={setShowMarkers} />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Easements</Label>
                  <Switch checked={showEasements} onCheckedChange={setShowEasements} />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Building Envelopes</Label>
                  <Switch checked={showEnvelopes} onCheckedChange={setShowEnvelopes} />
                </div>
              </CardContent>
            </Card>

            {/* Lots List */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Square className="w-4 h-4" />
                  Lots ({lots.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="max-h-[400px] overflow-y-auto">
                {lots.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No lots defined yet</p>
                ) : (
                  <div className="space-y-2">
                    {lots.map((lot) => (
                      <div 
                        key={lot.lot_id}
                        className={`p-2 rounded-lg border cursor-pointer transition-colors ${
                          selectedLot?.lot_id === lot.lot_id ? 'bg-primary/10 border-primary' : 'hover:bg-muted'
                        } ${editingLot?.lot_id === lot.lot_id ? 'ring-2 ring-yellow-500' : ''}`}
                        onClick={() => handleLotClick(lot)}
                        data-testid={`lot-item-${lot.lot_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">Lot {lot.lot_number}</span>
                          <div className="flex items-center gap-1">
                            {lot.center && (
                              <MapPin className="w-3 h-3 text-green-600" />
                            )}
                            {!lot.center && (
                              <MapPin className="w-3 h-3 text-muted-foreground opacity-30" />
                            )}
                          </div>
                        </div>
                        {lot.acreage && (
                          <p className="text-xs text-muted-foreground">{lot.acreage} acres</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Selected Lot Details */}
            {selectedLot && !editMode && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Lot {selectedLot.lot_number}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {selectedLot.center && (
                    <div>
                      <p className="text-muted-foreground">Coordinates:</p>
                      <p>{selectedLot.center.latitude.toFixed(6)}, {selectedLot.center.longitude.toFixed(6)}</p>
                    </div>
                  )}
                  {!selectedLot.center && (
                    <div className="text-yellow-600 text-xs">
                      ⚠️ No centroid set for this lot
                    </div>
                  )}
                  {selectedLot.acreage && (
                    <div>
                      <p className="text-muted-foreground">Acreage:</p>
                      <p>{selectedLot.acreage} acres</p>
                    </div>
                  )}
                  {selectedLot.notes && (
                    <div>
                      <p className="text-muted-foreground">Notes:</p>
                      <p className="text-xs">{selectedLot.notes}</p>
                    </div>
                  )}
                  {isBoard && (
                    <div className="flex gap-2 mt-3">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handleEditLot(selectedLot)}
                        data-testid="edit-lot-btn"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handleDeleteLot(selectedLot.lot_id)}
                        data-testid="delete-lot-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Map */}
          <div className="lg:col-span-3">
            <Card className="overflow-hidden">
              <div className="h-[600px]">
                {!loading && (
                  <MapContainer
                    center={HORSE_MOUNTAIN_CENTER}
                    zoom={DEFAULT_ZOOM}
                    style={{ height: "100%", width: "100%" }}
                    ref={mapRef}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    
                    <MapController center={mapCenter} zoom={DEFAULT_ZOOM} />
                    
                    {/* Render GeoJSON if available */}
                    {showLots && geojson && geojson.features && (
                      <GeoJSON 
                        key={JSON.stringify(geojson)}
                        data={geojson}
                        style={getFeatureStyle}
                        onEachFeature={onEachFeature}
                      />
                    )}
                    
                    {/* Render lot markers for ALL lots with centroids */}
                    {showMarkers && lots.filter(lot => lot.center).map((lot) => (
                      <Marker
                        key={lot.lot_id}
                        position={[lot.center.latitude, lot.center.longitude]}
                        eventHandlers={{
                          click: () => {
                            setSelectedLot(lot);
                            setMapCenter([lot.center.latitude, lot.center.longitude]);
                          }
                        }}
                      >
                        <Popup>
                          <div>
                            <strong>Lot {lot.lot_number}</strong><br/>
                            <small>{lot.center.latitude.toFixed(6)}, {lot.center.longitude.toFixed(6)}</small><br/>
                            {lot.acreage && <small>{lot.acreage} acres</small>}
                          </div>
                        </Popup>
                      </Marker>
                    ))}

                    {/* Edit mode draggable marker */}
                    {editMode && editMarkerPosition && (
                      <DraggableMarker
                        position={editMarkerPosition}
                        onPositionChange={handleEditMarkerDrag}
                        draggable={true}
                      />
                    )}
                  </MapContainer>
                )}
                {loading && (
                  <div className="h-full flex items-center justify-center bg-muted">
                    <p className="text-muted-foreground">Loading map...</p>
                  </div>
                )}
              </div>
            </Card>
            
            {/* Map Legend */}
            <div className="mt-4 flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: "#2F4F4F", opacity: 0.5 }}></div>
                <span>Lot Boundaries</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded border-2" style={{ borderColor: "#C49A6C" }}></div>
                <span>Property Lines</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-600" />
                <span>Lot Centroid</span>
              </div>
              {editMode && (
                <div className="flex items-center gap-2 text-yellow-600">
                  <Edit className="w-4 h-4" />
                  <span>Editing Mode Active</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Wrap with AdminSidebar for board mode
  if (isBoard) {
    return <AdminSidebar>{content}</AdminSidebar>;
  }

  return content;
};

export default GISPage;
